<template>
    <PageWrapper>
        <template #headline>
            <fa icon="fa-solid fa-file-signature" />&nbsp;
            {{ $t('payment_account') }}&nbsp;
            {{ paymentAccount.id }}
        </template>
        <template #actions>
            <BackButton
                v-if="$router.options.history.state.back"
                @click="router.go(-1)"
            />
            <BackButton
                v-if="
                    $router.options.history.state.back !== '/payment-accounts'
                "
                icon="fa-list"
                title="payment_accounts"
                @click="toPaymentAccounts"
            />
            <EditButton
                title="edit"
                @click="edit"
            />
            <DeleteButton @click="handleDelatePaymentAccount" />
        </template>

        <SummaryWrapper>
            <PaymentAccountSummary />
        </SummaryWrapper>

        <PaymentAccountPlatformsTable
            v-if="paymentAccountPlatforms.length > 0"
        />

        <PaymentAccountAssetsTable v-if="paymentAccountAssets.length > 0" />

        <AuditsTable
            v-if="paymentAccount.id"
            :initial-filters="{
                auditable_id: paymentAccount.id,
                auditable_type: auditedModels.payment_account.value,
            }"
        />

        <!--
        <AuditsTable
            v-if="contract.contract_uid"
            :initial-filters="{
                auditable_id: contract.contract_uid,
                auditable_type: auditedModels.contract.value,
            }"
        /> -->
        <ModalWrapper v-if="isModalOpen" />
    </PageWrapper>
</template>

<script setup lang="ts">
import BackButton from '@partials/actions/BackButton.vue'
import DeleteButton from '@partials/actions/DeleteButton.vue'
import EditButton from '@partials/actions/EditButton.vue'
import PageWrapper from '@partials/pages/PageWrapper.vue'
import { NameAndId } from '@src/components'

import moment from 'moment'

import { ModalButtonStyles, useEnum, useModal } from '@composables/common'
import { useSummary } from '@src/composables/common'

import { usePaymentAccountAssetsTable } from '@src/composables/paymentAccounts/usePaymentAccountAssetsTable'
import { usePaymentAccountPlatformsTable } from '@src/composables/paymentAccounts/usePaymentAccountPlatformsTable'
import { PaymentAccountType } from '@src/enums/PaymentAccount'
import AuditsTable from '@src/partials/audits/AuditsTable.vue'
import { Asset, Platform } from '@src/types'
import { PaymentAccountDetails } from '@src/types/PaymentAccount'
import { SummaryItem } from '@src/types/Summary'
import {
    deletePaymentAccount as apiDeletePaymentAccount,
    getPaymentAccount,
} from '@src/utils/api/paymentAccounts'
import { formatNumericValues } from '@src/utils/helpers'
import { useAuditsStore } from '@src/utils/stores/audits'
import { useToastStore } from '@src/utils/stores/toast'
import { storeToRefs } from 'pinia'
import { computed, h, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import SummaryWrapper from '@src/components/SummaryWrapper.vue'

const router = useRouter()
const route = useRoute()
const toast = useToastStore()
const i18n = useI18n()
const auditsStore = useAuditsStore()

const { getEnumLabel } = useEnum()
const { ModalWrapper, openModal } = useModal()

const paymentAccount = ref<PaymentAccountDetails>(new PaymentAccountDetails())
const paymentAccountPlatforms = ref<Platform[]>([])
const paymentAccountAssets = ref<Asset[]>([])

const isModalOpen = ref(false)

const { PaymentAccountPlatformsTable } = usePaymentAccountPlatformsTable(
    paymentAccountPlatforms
)
const { PaymentAccountAssetsTable } =
    usePaymentAccountAssetsTable(paymentAccountAssets)
const { auditedModels } = storeToRefs(auditsStore)

const paymentAccountDetailsItems = computed<SummaryItem[]>(
    (): SummaryItem[] => {
        return [
            {
                label: i18n.t('id'),
                value: paymentAccount.value.id,
                copy: true,
            },
            {
                label: i18n.t('type'),
                value: getEnumLabel(
                    PaymentAccountType,
                    paymentAccount.value.type
                ),
            },
            {
                label: i18n.t('name'),
                value: paymentAccount.value.name,
            },
            {
                label: i18n.t('description'),
                value: paymentAccount.value.description,
            },
            {
                label: i18n.t('webhook_api_key'),
                value: paymentAccount.value.webhook_api_key,
                condition:
                    paymentAccount.value.type === PaymentAccountType.LEMONWAY,
            },
            {
                label: i18n.t('payment_reference_prefix'),
                value: paymentAccount.value.payment_reference_prefix,
                condition:
                    paymentAccount.value.type === PaymentAccountType.LEMONWAY,
            },
            {
                label: i18n.t('payment_endpoint'),
                value: paymentAccount.value.payment_endpoint,
                condition:
                    paymentAccount.value.type === PaymentAccountType.LEMONWAY,
            },
            {
                label: i18n.t('auth_endpoint'),
                value: paymentAccount.value.auth_endpoint,
                condition:
                    paymentAccount.value.type === PaymentAccountType.LEMONWAY,
            },
            {
                label: i18n.t('api_endpoint'),
                value: paymentAccount.value.api_endpoint,
            },
            {
                label: i18n.t('api_token'),
                value: paymentAccount.value.api_token,
                condition:
                    paymentAccount.value.type === PaymentAccountType.LEMONWAY,
            },
            {
                label: i18n.t('client_id'),
                value: paymentAccount.value.client_id,
                condition:
                    paymentAccount.value.type === PaymentAccountType.SECUPAY,
            },
            {
                label: i18n.t('client_secret'),
                value: paymentAccount.value.client_secret,
                condition:
                    paymentAccount.value.type === PaymentAccountType.SECUPAY,
            },
            {
                label: i18n.t('master_contract_id'),
                value: paymentAccount.value.master_contract_id,
                condition:
                    paymentAccount.value.type === PaymentAccountType.SECUPAY,
            },
            {
                label: i18n.t('checkout_template_id'),
                value: paymentAccount.value.checkout_template_id,
                condition:
                    paymentAccount.value.type === PaymentAccountType.SECUPAY,
            },
            {
                label: i18n.t('sync_enabled'),
                value: paymentAccount.value.sync_enabled,
            },
            {
                label: i18n.t('currency'),
                value: paymentAccount.value.currency,
                condition:
                    paymentAccount.value.type === PaymentAccountType.LEMONWAY,
            },
            {
                label: i18n.t('deposit_account_holder'),
                value: paymentAccount.value.deposit_account_holder,
                condition:
                    paymentAccount.value.type === PaymentAccountType.LEMONWAY,
            },
            {
                label: i18n.t('deposit_iban'),
                value: paymentAccount.value.deposit_iban,
                condition:
                    paymentAccount.value.type === PaymentAccountType.LEMONWAY,
            },
            {
                label: i18n.t('deposit_bic'),
                value: paymentAccount.value.deposit_bic,
                condition:
                    paymentAccount.value.type === PaymentAccountType.LEMONWAY,
            },
            {
                label: i18n.t('limit_credit_card_order'),
                value: formatNumericValues(
                    paymentAccount.value?.limit_credit_card_order ?? 0,
                    paymentAccount.value.currency
                ),
            },
            {
                label: i18n.t('limit_credit_card_deposit'),
                value: formatNumericValues(
                    paymentAccount.value?.limit_credit_card_deposit ?? 0,
                    paymentAccount.value.currency
                ),
            },
            {
                label: i18n.t('limit_bank_transfer_order'),
                value: formatNumericValues(
                    paymentAccount.value?.limit_bank_transfer_order ?? 0,
                    paymentAccount.value.currency
                ),
            },
            {
                label: i18n.t('limit_bank_transfer_deposit'),
                value: formatNumericValues(
                    paymentAccount.value?.limit_bank_transfer_deposit ?? 0,
                    paymentAccount.value.currency
                ),
            },
            {
                label: i18n.t('limit_bank_transfer_withdrawal'),
                value: formatNumericValues(
                    paymentAccount.value?.limit_bank_transfer_withdrawal ?? 0,
                    paymentAccount.value.currency
                ),
            },
            {
                label: i18n.t('deposit_account_holder_address'),
                value: paymentAccount.value.deposit_account_holder_address,
                condition:
                    paymentAccount.value.type === PaymentAccountType.LEMONWAY,
            },
            {
                label: i18n.t('deposit_account_holder_country'),
                value: paymentAccount.value.deposit_account_holder_country,
                condition:
                    paymentAccount.value.type === PaymentAccountType.LEMONWAY,
            },
            {
                label: i18n.t('healthcheck_wallet'),
                value: paymentAccount.value.healthcheck_wallet,
                condition:
                    paymentAccount.value.type === PaymentAccountType.LEMONWAY,
            },
            {
                label: i18n.t('uses_proxy'),
                value: paymentAccount.value.uses_proxy,
                condition:
                    paymentAccount.value.type === PaymentAccountType.LEMONWAY,
            },
            {
                label: i18n.t('enable_stakeholder_payment'),
                value: paymentAccount.value.enable_stakeholder_payment,
                condition:
                    paymentAccount.value.type === PaymentAccountType.SECUPAY,
            },
            {
                label: i18n.t('stakeholder_contract_id'),
                value: paymentAccount.value.stakeholder_contract_id,
                condition:
                    paymentAccount.value.type === PaymentAccountType.SECUPAY &&
                    paymentAccount.value.enable_stakeholder_payment,
            },
            {
                label: i18n.t('stakeholder_fee'),
                value: paymentAccount.value.stakeholder_fee,
                condition:
                    paymentAccount.value.type === PaymentAccountType.SECUPAY &&
                    paymentAccount.value.enable_stakeholder_payment,
            },
            {
                label: i18n.t('created_on'),
                value: moment(paymentAccount.value.created_at).format(
                    'DD.MM.yyyy'
                ),
                condition: !!paymentAccount.value.created_at,
            },
            {
                label: i18n.t('updated_at'),
                value: moment(paymentAccount.value.updated_at).format(
                    'DD.MM.yyyy'
                ),
                condition: !!paymentAccount.value.updated_at,
            },
        ]
    }
)

const { Summary: PaymentAccountSummary } = useSummary(
    paymentAccountDetailsItems,
    {
        title: 'summary',
    }
)

async function loadPaymentAccount() {
    const id = route.params.id as string

    if (!id) {
        return
    }

    const paymentAccountRequest = await getPaymentAccount(id)
    paymentAccount.value = paymentAccountRequest.data
    paymentAccountPlatforms.value = paymentAccount.value.platforms || []
    paymentAccountAssets.value = paymentAccount.value.assets || []
}

async function deletePaymentAccount(paymentAccount: PaymentAccountDetails) {
    await apiDeletePaymentAccount(paymentAccount)
    toast.success(
        i18n.t('toast_success_payment_account_deleted', {
            paymentAccount: paymentAccount.id,
        })
    )
    isModalOpen.value = false
    toPaymentAccounts()
}

function handleDelatePaymentAccount() {
    isModalOpen.value = true
    openModal(
        {
            id: 'delete_payment_account-modal',
            title: 'modal_title_delete_payment_account',
            open: isModalOpen,
            onCancel: () => (isModalOpen.value = false),
            onSubmit: async () =>
                await deletePaymentAccount(paymentAccount.value),
        },
        () =>
            h(NameAndId, {
                idKey: 'id',
                nameKey: 'type',
                modelValue: [paymentAccount.value],
            }),
        {
            submitButtonText: 'delete',
            styles: {
                submitButton: ModalButtonStyles['RED'],
            },
        }
    )
}

function toPaymentAccounts() {
    router.push({
        name: 'payment-accounts',
    })
}

function edit() {
    router.push({
        name: 'payment_account.edit',
        params: { id: paymentAccount.value.id },
    })
}

watch(
    () => route.params.id,
    async () => {
        await loadPaymentAccount()
    }
)

onMounted(async () => {
    await loadPaymentAccount()
})
</script>

<style scoped></style>
